import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Form, Input } from 'antd'
import { useTranslation } from 'react-i18next'

import { routes } from '../../../navigation/Router'

import '../index.less'
import { SpinLoader } from '../../common/SpinLoader'
import { Challenge, resolveChallenge, Solution } from '@icure/cardinal-sdk'
import { MSG_GW_URL, SPEC_ID } from '../../../constants'
import { KerberusWidget } from '../KerberusWidget'
import { createPortal } from 'react-dom'
import { ModalLegalDocument } from '../ModalLegalDocument'
import { setWaitingForToken } from '../../../core/services/auth.api'
import { useAppDispatch } from '../../../core/hooks'

const CHALLENGE_VALIDITY_MS = 2 * 60 * 1000 // 2 minutes

interface LoginFormProps {
  state: 'initialised' | 'loading' | 'waitingForToken'
  requestNewShortToken: () => void
  submitEmailForTokenRequest: (email: string, captchaToken: Solution) => void
  submitEmailAndValidationTokenForAuthentication: (validationCode: string) => void
  startAuthenticationProcessWithNewCaptchaToken: (captchaToken: Solution) => void
  refusedEnvironmentSelection?: boolean
  invalidToken?: boolean
  invalidEmail?: boolean
  newShortTokenIsRequested?: boolean
}

const LoginForm: React.FC<LoginFormProps> = ({
  state,
  submitEmailForTokenRequest,
  submitEmailAndValidationTokenForAuthentication,
  requestNewShortToken,
  startAuthenticationProcessWithNewCaptchaToken,
  invalidToken,
  invalidEmail,
  refusedEnvironmentSelection,
  newShortTokenIsRequested,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [loginForm] = Form.useForm()
  const requestIdRef = useRef(0)
  const [captchaToken, setCaptchaToken] = useState<Solution | undefined>(undefined)
  const [captchaTokenTimestamp, setCaptchaTokenTimestamp] = useState<number>(0)
  const [challengeRefreshCounter, setChallengeRefreshCounter] = useState(0)
  const pendingSubmitEmailRef = useRef<string | undefined>(undefined)
  const [progress, setProgress] = useState<number | undefined>(undefined)
  const [isTermsOfUseModalOpen, setTermsOfUseModalOpen] = useState(false)
  const [isPrivacyPolicyModalOpen, setPrivacyPolicyModalOpen] = useState(false)
  const [showResendHelper, setShowResendHelper] = useState(false)

  useEffect(() => {
    if (refusedEnvironmentSelection) {
      loginForm.resetFields(['validationCode'])
      setShowResendHelper(false)
      console.log({ showResendHelper })
      dispatch(setWaitingForToken(false))
    }
  }, [refusedEnvironmentSelection])

  useEffect(() => {
    if (invalidEmail) {
      setCaptchaToken(undefined)
      setChallengeRefreshCounter((c) => c + 1)
    }
  }, [invalidEmail])

  useEffect(() => {
    if (state !== 'waitingForToken' && !captchaToken) return
    if (refusedEnvironmentSelection && state !== 'waitingForToken') return
    const timer = setTimeout(() => {
      setShowResendHelper(true)
    }, 10000) // 10 seconds delay

    return () => clearTimeout(timer) // Cleanup on unmount
  }, [state, refusedEnvironmentSelection])

  useEffect(() => {
    if (!SPEC_ID) {
      console.error('No spec id found')
      return
    }
    const requestId = ++requestIdRef.current
    let running = true
    setCaptchaToken(undefined)
    fetch(`${MSG_GW_URL}/${SPEC_ID}/challenge`)
      .then((x) => x.json())
      .then((challenge: Challenge) => {
        if (!running) throw new Error('Cancelled Kerberus on LoginForm')
        if (!SPEC_ID) throw new Error('Cancelled Kerberus on LoginForm because of missing SPEC_ID')
        return resolveChallenge(challenge, SPEC_ID, undefined, (progress) => {
          if (!running || requestId !== requestIdRef.current) return
          setProgress(progress * 100)
        })
      })
      .then((solution) => {
        if (!running || requestId !== requestIdRef.current) return
        setProgress(undefined)
        setCaptchaToken(solution)
        setCaptchaTokenTimestamp(Date.now())
        if (newShortTokenIsRequested) {
          startAuthenticationProcessWithNewCaptchaToken(solution)
        } else if (pendingSubmitEmailRef.current) {
          const email = pendingSubmitEmailRef.current
          pendingSubmitEmailRef.current = undefined
          submitEmailForTokenRequest(email, solution)
        }
      })
      .catch((e) => {
        if (!running || requestId !== requestIdRef.current) return
        setProgress(undefined)
        pendingSubmitEmailRef.current = undefined
        console.warn(e)
      })
    return () => {
      running = false
    }
  }, [newShortTokenIsRequested, challengeRefreshCounter])

  const handleResentValidationCode = () => {
    requestNewShortToken()
    loginForm.resetFields(['validationCode'])
    setShowResendHelper(false)
    dispatch(setWaitingForToken(true))
  }

  const isChallengeExpired = useCallback(() => {
    return !captchaToken || !captchaTokenTimestamp || Date.now() - captchaTokenTimestamp > CHALLENGE_VALIDITY_MS
  }, [captchaToken, captchaTokenTimestamp])

  const handleSubmit = (values: { email: string; validationCode: string }) => {
    const { email, validationCode } = values

    if (email.length === 0) {
      return
    }

    if (state === 'waitingForToken') {
      if (validationCode.length === 0) {
        return
      }
      submitEmailAndValidationTokenForAuthentication(validationCode)
      setShowResendHelper(false)
    } else {
      if (isChallengeExpired()) {
        pendingSubmitEmailRef.current = email
        setChallengeRefreshCounter((c) => c + 1)
        return
      }
      // captchaToken is guaranteed to be defined here since isChallengeExpired() checks it
      submitEmailForTokenRequest(email, captchaToken as Solution)
    }
  }

  return (
    <>
      {state === 'loading' && <SpinLoader />}
      <Form form={loginForm} onFinish={(values) => handleSubmit(values)} className="auth-form" layout="vertical">
        <div className="auth-form__title">
          <h2>{t('loginForm.title')}</h2>
        </div>
        <div className="auth-form__inputs">
          <Form.Item
            name="email"
            label={t('loginForm.emailLabel')}
            rules={[{ required: true, message: t('loginForm.emailRequired') }]}
            normalize={(value: string) => (value ? value.replace(/\s+/g, '') : value)}
          >
            <Input placeholder={t('loginForm.usernamePlaceholder')} size="large" />
          </Form.Item>

          {state === 'waitingForToken' && (
            <Form.Item
              name="validationCode"
              label={t('loginForm.validationCodeLabel')}
              rules={[
                { required: true, message: t('loginForm.validationCodeRequired') },
                {
                  min: 6,
                  max: 6,
                  message: t('loginForm.validationCodeIsTooShort'),
                },
              ]}
              validateStatus={invalidToken ? 'error' : ''}
              help={invalidToken ? t('loginForm.invalidToken') : undefined}
              normalize={(value: string) => (value ? value.replace(/\s+/g, '') : value)}
            >
              <Input count={{ max: 6 }} placeholder={t('loginForm.passwordPlaceholder')} size="large" />
            </Form.Item>
          )}
        </div>
        {showResendHelper && (
          <div className="auth-form__textHelper auth-form__textHelper--centered">
            <p>
              {t('loginForm.noCodeReceived')}
              <button type="button" className="link" onClick={handleResentValidationCode}>
                {t('loginForm.resendCode')}
              </button>
            </p>
          </div>
        )}
        {!!progress && <KerberusWidget progress={progress} />}
        <Button type="primary" size="large" htmlType="submit" disabled={(state === 'initialised' && !captchaToken) || state === 'loading'}>
          {state === 'waitingForToken' ? t('loginForm.submit') : t('loginForm.sendValidationCode')}
        </Button>
        <div className="auth-form__textHelper">
          <p>
            {t('loginForm.termsPrefix', { defaultValue: 'By signing in, you accept our' })}{' '}
            <button type="button" className="link" onClick={() => setTermsOfUseModalOpen(true)}>
              {t('loginForm.termsOfUse')}
            </button>{' '}
            {t('loginForm.and', { defaultValue: 'and our' })}{' '}
            <button type="button" className="link" onClick={() => setPrivacyPolicyModalOpen(true)}>
              {t('loginForm.privacyPolicy')}
            </button>
          </p>
          <p>
            {t('loginForm.notRegistered')}{' '}
            <Link className="link" to={routes.register}>
              {t('loginForm.registerLink')}
            </Link>
          </p>
        </div>
      </Form>
      {isTermsOfUseModalOpen &&
        createPortal(
          <ModalLegalDocument
            handleClose={() => {
              setTermsOfUseModalOpen(false)
            }}
            display={isTermsOfUseModalOpen}
            documentType="tou"
          />,
          document.body,
        )}
      {isPrivacyPolicyModalOpen &&
        createPortal(
          <ModalLegalDocument
            handleClose={() => {
              setPrivacyPolicyModalOpen(false)
            }}
            display={isPrivacyPolicyModalOpen}
            documentType="pp"
          />,
          document.body,
        )}
    </>
  )
}

export default LoginForm
